import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  roomBySearch,
  roomByStages,
  roomBySteps,
  roomByTypes,
} from '../../../shared/configs/filterOptions';
import { selectors, sliceKeys, roomsFilterNames } from '../../../model';
import { useFilteredData, filterTypes } from '../../../shared/packages/filter';
import ColListWrapper from '../../../shared/ui/ColListWrapper';
import Action from './Action';
import InfoBlock from './InfoBlock';
import { roomsSelectors } from '../../../api/rooms';
import BaseRoomItem from '../../../shared/ui/BaseRoomItem';
import NotFound from '../../../shared/ui/NotFound';
import RoomsHeader from './RoomsHeader';

const { selectSearch, selectFilter } = selectors[sliceKeys.rooms];
const { createHasRoomRelationsSelector } = selectors[sliceKeys.houses];

const filterOptions = {
  [roomsFilterNames.stages]: roomByStages,
  [roomsFilterNames.steps]: roomBySteps,
  [roomsFilterNames.types]: roomByTypes,
};

const MemoizedItem = memo(FirstLevelItem);

const RoomsSelection = ({ data, children }) => {
  const search = useSelector(selectSearch);
  const filteredDataBySearch = useFilteredData({
    data,
    value: search,
    filterType: filterTypes.bySearch,
    options: roomBySearch,
  });

  const filter = useSelector(selectFilter);
  const filteredData = useFilteredData({
    data: filteredDataBySearch,
    value: filter,
    filterType: filterTypes.byParams,
    options: filterOptions,
  });

  return (
    <>
      {children}
      {filteredData.length ? (
        <ColListWrapper grouped scrollable>
          {roomsSelectors.groupByHouses(filteredData).map((item) => (
            <MemoizedItem key={item.name} item={item} />
          ))}
        </ColListWrapper>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default RoomsSelection;

function FirstLevelItem({ item }) {
  return (
    <li>
      <RoomsHeader item={item} />
      <ColListWrapper>
        {item.rooms.map((room) => (
          <Item key={room.id} room={room} />
        ))}
      </ColListWrapper>
    </li>
  );
}

function Item({ room }) {
  const selectHasRoomRelations = useMemo(
    () => createHasRoomRelationsSelector(room),
    [room]
  );

  const hasRelations = useSelector(selectHasRoomRelations);

  return (
    <BaseRoomItem
      room={room}
      action={<Action room={room} hasRelations={hasRelations} />}
      info={hasRelations && <InfoBlock room={room} />}
    />
  );
}
